import { IComposableChartsWidget } from 'wisestep-api';

import { postgresCallsDashboardKeys } from '../../../features/composableAnalytics/DerivedCharts/Calls/helper/postgresCallsDashboardKeys';
import { emailsChartsKeyPostgre } from '../../../features/composableAnalytics/DerivedCharts/emails/helper/postgresEmailsDashboardKeys';
import { postgresFollowUpDashboardKeys } from '../../../features/composableAnalytics/DerivedCharts/followUps/helper/postgresFollowUpDashboardKeys';
import { postgreMappingsDashboardKeys } from '../../../features/composableAnalytics/DerivedCharts/mappings/helper/postgreMappingsDashboardKeys';
import { postgresRTRAnalyticsDashboardKeys } from '../../../features/composableAnalytics/DerivedCharts/rtr/helper/postgresRTRAnalyticsDashboardKeys';
import {
  smsWidgetsDashboardKeyPostgre,
  applicationWidgetKeyPostgre,
  ChartScope,
  clientNotesChartsKeyPostgre,
} from '../../composableAnalytics';
import { leadStaticChartsKeys } from '../../composableAnalytics/BaseCharts/lead/leadDashboardKeys';
import { cvUploadChartsKeyPostgre } from '../../composableAnalytics/DerivedCharts/cvUpload/helper/postgreCvUploadDashboardsKeys';
import { cvViewChartsKeyPostgre } from '../../composableAnalytics/DerivedCharts/cvView/helper/postgreCvViewDashboardKeys';
import { jobsChartsKeyPostgre } from '../../composableAnalytics/DerivedCharts/Jobs/helper';
import { potentialRevenueStaticChartsKey } from '../../composableAnalytics/DerivedCharts/PotentialRevenue/helper/potentialRevenueDashboardkeys';
import { submissionsChartsKeyPostgre } from '../../composableAnalytics/DerivedCharts/submissions/helper/postgreSubmissionDashboardKeys';
import { freeTextWidgetKeys } from '../../composableAnalytics/Widgets/helper/freeTextWidgetKeys';
import WidgetsList from '../components/dashboard/WidgetsList';

export const getCandidateJobStatus = (businessDefinition) =>
  businessDefinition.jobOption === 'open_jobs'
    ? 'Opened'
    : businessDefinition.jobOption === 'closed_jobs'
    ? 'Closed'
    : 'Any';

export type ITimePeriod = { id: string; label: string; filterDate?: string };
type IWidgets = { label: string; widgets: JSX.Element };

export const freeTextWidget = () => [
  {
    label: '',
    widgets: <WidgetsList items={freeTextWidgetKeys} />,
  },
];

export const postgreSubmissionChartsByTimePeriod = (
  selectedScope: ChartScope,
  timePeriodList: ITimePeriod[]
): IWidgets[] =>
  timePeriodList.map((timePeriod) => ({
    label: timePeriod.label,
    widgets: <WidgetsList items={submissionsChartsKeyPostgre(timePeriod.id, selectedScope)} />,
  }));

export const postgreCvUploadChartsByTimePeriod = (
  selectedScope: ChartScope,
  timePeriodList: ITimePeriod[]
): IWidgets[] =>
  timePeriodList.map((timePeriod) => ({
    label: timePeriod.label,
    widgets: <WidgetsList items={cvUploadChartsKeyPostgre(timePeriod.id, selectedScope)} />,
  }));

export const postgreJobsChartsByTimePeriod = (
  selectedScope: ChartScope,
  timePeriodList: ITimePeriod[]
): IWidgets[] =>
  timePeriodList.map((timePeriod) => ({
    label: timePeriod.label,
    widgets: <WidgetsList items={jobsChartsKeyPostgre(timePeriod.id, selectedScope)} />,
  }));

export const postgreClientNotesChartsByTimePeriod = (
  selectedScope: ChartScope,
  timePeriodList: ITimePeriod[]
): IWidgets[] =>
  timePeriodList.map((timePeriod) => ({
    label: timePeriod.label,
    widgets: <WidgetsList items={clientNotesChartsKeyPostgre(timePeriod.id, selectedScope)} />,
  }));

export const potentialRevenueChartsByTimePeriod = (
  selectedScope: ChartScope,
  timePeriodList: ITimePeriod[],
  potentialRevenueForBDefChartsKeys: (
    timePeriod: any,
    selectedScope?: ChartScope
  ) => IComposableChartsWidget[]
): IWidgets[] =>
  timePeriodList.map((timePeriod) => ({
    label: timePeriod.label,
    widgets: (
      <WidgetsList
        items={[
          ...potentialRevenueStaticChartsKey(timePeriod.id, selectedScope),
          ...potentialRevenueForBDefChartsKeys(timePeriod, selectedScope),
        ]}
      />
    ),
  }));

export const postgreBusinessDefinitionChartsByTimePeriod = (
  selectedScope: ChartScope,
  timePeriodList: ITimePeriod[],
  businessDefinitionChartsKeysPostgre: (
    timePeriod: any,
    selectedScope?: ChartScope
  ) => IComposableChartsWidget[]
): IWidgets[] =>
  timePeriodList.map((timePeriod) => ({
    label: timePeriod.label,
    widgets: <WidgetsList items={businessDefinitionChartsKeysPostgre(timePeriod, selectedScope)} />,
  }));

export const postgreLeadChartsByTimePeriod = (
  selectedScope: ChartScope,
  timePeriodList: ITimePeriod[],
  leadChartsByStatusKeys: (timePeriod: any, selectedScope?: ChartScope) => IComposableChartsWidget[]
): IWidgets[] =>
  timePeriodList.map((timePeriod) => ({
    label: timePeriod.label,
    widgets: (
      <WidgetsList
        items={[
          ...leadStaticChartsKeys(timePeriod.id, selectedScope),
          ...leadChartsByStatusKeys(timePeriod, selectedScope),
        ]}
      />
    ),
  }));

export const postgreMappingsChartsByTimePeriod = (
  selectedScope: ChartScope,
  timePeriodList: ITimePeriod[]
): IWidgets[] =>
  timePeriodList.map((timePeriod) => ({
    label: timePeriod.label,
    widgets: <WidgetsList items={postgreMappingsDashboardKeys(timePeriod.id, selectedScope)} />,
  }));

export const postgreSmsWidgetsByTimePeriod = (
  selectedScope: ChartScope,
  timePeriodList: ITimePeriod[]
): IWidgets[] =>
  timePeriodList.map((timePeriod) => ({
    label: timePeriod.label,
    widgets: <WidgetsList items={smsWidgetsDashboardKeyPostgre(timePeriod?.id, selectedScope)} />,
  }));

export const postgreCallsWidgetsByTimePeriod = (
  selectedScope: ChartScope,
  timePeriodList: ITimePeriod[]
): IWidgets[] =>
  timePeriodList.map((timePeriod) => ({
    label: timePeriod.label,
    widgets: <WidgetsList items={postgresCallsDashboardKeys(timePeriod?.id, selectedScope)} />,
  }));

export const postgreEmailsChartsByTimePeriod = (
  selectedScope: ChartScope,
  timePeriodList: ITimePeriod[]
): IWidgets[] =>
  timePeriodList.map((timePeriod) => ({
    label: timePeriod.label,
    widgets: <WidgetsList items={emailsChartsKeyPostgre(timePeriod.id, selectedScope)} />,
  }));

export const postgreApplicationWidgetsByTimePeriod = (
  selectedScope: ChartScope,
  timePeriodList: ITimePeriod[]
): IWidgets[] =>
  timePeriodList.map((timePeriod) => ({
    label: timePeriod.label,
    widgets: <WidgetsList items={applicationWidgetKeyPostgre(timePeriod.id, selectedScope)} />,
  }));

export const postgresFollowUpWidgetsByTimePeriod = (
  selectedScope: ChartScope,
  timePeriodList: ITimePeriod[]
): IWidgets[] =>
  timePeriodList.map((timePeriod) => ({
    label: timePeriod.label,
    widgets: <WidgetsList items={postgresFollowUpDashboardKeys(timePeriod.id, selectedScope)} />,
  }));

export const postgreCvViewWidgetsByTimePeriod = (
  selectedScope: ChartScope,
  timePeriodList: ITimePeriod[]
): IWidgets[] =>
  timePeriodList.map((timePeriod) => ({
    label: timePeriod.label,
    widgets: <WidgetsList items={cvViewChartsKeyPostgre(timePeriod.id, selectedScope)} />,
  }));

export const postgreRTRAnalyticsWidgetsByTimePeriod = (
  selectedScope: ChartScope,
  timePeriodList: ITimePeriod[]
): IWidgets[] =>
  timePeriodList.map((timePeriod) => ({
    label: timePeriod.label,
    widgets: (
      <WidgetsList items={postgresRTRAnalyticsDashboardKeys(timePeriod.id, selectedScope)} />
    ),
  }));
